/**
 * The FOMO social layer (docs/API.md "Social"): the live activity tape, reactions on posts,
 * recent trades on token pages, web push subscriptions and share cards. Additive to the milestone 3 contract.
 */
import { z } from 'zod'
import { Address, Cursor, DecimalString, Hash32, IsoTimestamp, Uuid, UsdString } from '../primitives.js'
import { AgentSummary, JsonObject } from './common.js'

// Activity ----------------------------------------------------------------------------------

/** What happened: a fill, a token launch, a closed fee epoch, a claimed reward, an agent going live, a milestone. */
export const ActivityKind = z.enum(['trade', 'launch', 'epoch_closed', 'reward_claimed', 'agent_activated', 'milestone', 'challenge'])
export type ActivityKind = z.output<typeof ActivityKind>

/**
 * One compact event of the live tape (`social.activity`). `id` is the tape's sequence number as a decimal string;
 * it is also the SSE event id, so a reconnect with `Last-Event-ID` resumes after it.
 */
export const ActivityItem = z.object({
  id: z.string().regex(/^[1-9][0-9]*$/),
  kind: ActivityKind,
  agent: AgentSummary.nullable(),
  /** One line, ready to print: "Nova bought 1.2k WRLD for $42". */
  summary: z.string(),
  /** USD size when known (trades: the execution's notional). */
  amountUsd: UsdString.nullable(),
  symbol: z.string().nullable(),
  txHash: Hash32.nullable(),
  /** In-app path to open (`/a/:handle`, `/tokens/:address`, …). */
  href: z.string().nullable(),
  payload: JsonObject,
  occurredAt: IsoTimestamp,
})
export type ActivityItem = z.output<typeof ActivityItem>

/** `GET /activity`: newest first; `cursor` is the previous page's `nextCursor`. */
export const ActivityQuery = z.object({ cursor: Cursor.optional(), limit: z.coerce.number().int().min(1).max(100).default(30) })

// Reactions ---------------------------------------------------------------------------------

export const ReactionKind = z.enum(['fire', 'eyes', 'skull', 'rocket'])
export type ReactionKind = z.output<typeof ReactionKind>

export const ReactionCounts = z.object({ fire: z.number().int().nonnegative(), eyes: z.number().int().nonnegative(), skull: z.number().int().nonnegative(), rocket: z.number().int().nonnegative() })
export type ReactionCounts = z.output<typeof ReactionCounts>

/** Reaction totals of one post and the caller's own reactions (empty when signed out). */
export const PostReactions = z.object({ postId: Uuid, counts: ReactionCounts, mine: z.array(ReactionKind) })
export type PostReactions = z.output<typeof PostReactions>

export const ReactionBody = z.object({ kind: ReactionKind })
export type ReactionBody = z.input<typeof ReactionBody>

/** `GET /posts/reactions?ids=a,b,c`: up to 100 post ids, comma-separated. */
export const PostReactionsQuery = z.object({
  ids: z
    .string()
    .min(1)
    .transform((s, ctx) => {
      const ids = [...new Set(s.split(',').map((x) => x.trim()).filter(Boolean))]
      if (ids.length === 0 || ids.length > 100 || !ids.every((id) => Uuid.safeParse(id).success)) {
        ctx.addIssue({ code: 'custom', message: 'ids: 1 to 100 comma-separated uuids' })
        return z.NEVER
      }
      return ids
    }),
})

// Token trades ------------------------------------------------------------------------------

/**
 * A recent buy or sell of an agent token: pool swaps from arbitrage's ClickHouse (`dex.swaps`, any wallet) and fills
 * of algo.boo agents (their public trade posts; the platform holds the orders). `amountQuote` is the quote side in whole
 * units (decimal string).
 */
export const TokenTrade = z.object({
  id: z.string(),
  side: z.enum(['buy', 'sell']),
  wallet: Address.nullable(),
  /** The algo.boo agent behind the fill, when it was one. */
  agent: AgentSummary.nullable(),
  amountQuote: DecimalString.nullable(),
  quoteSymbol: z.string().nullable(),
  notionalUsd: UsdString.nullable(),
  txHash: Hash32.nullable(),
  at: IsoTimestamp,
  /** The wallet is an active copy-trade leader some agent selected from analytics (top profitable wallets). */
  smartMoney: z.boolean(),
  /** How many agents copy this wallet (active `agents.agent_leaders` rows). */
  copiedBy: z.number().int().nonnegative(),
  source: z.enum(['pool', 'agent']),
})
export type TokenTrade = z.output<typeof TokenTrade>

export const TokenTradesQuery = z.object({ limit: z.coerce.number().int().min(1).max(50).default(20) })

export const TokenTrades = z.object({
  address: Address,
  holdersCount: z.number().int().nonnegative(),
  /** Holders that first appeared in the last 24 h (new `token_holders` rows with a balance). */
  holders24hDelta: z.number().int(),
  trades: z.array(TokenTrade),
  /**
   * Whether the pool swaps (`source: 'pool'`) could be read from arbitrage's ClickHouse. `available: false` means
   * `trades` holds agent fills only and is not the token's whole tape; `reason` says why.
   */
  poolSwaps: z.object({ available: z.boolean(), reason: z.string().nullable() }),
})
export type TokenTrades = z.output<typeof TokenTrades>

// Web push ----------------------------------------------------------------------------------

/** `PushSubscription.toJSON()` from the browser. */
export const PushSubscriptionInput = z.object({
  endpoint: z.url({ protocol: /^https$/ }).max(2000),
  keys: z.object({ p256dh: z.string().min(40).max(200).regex(/^[A-Za-z0-9_-]+={0,2}$/), auth: z.string().min(8).max(64).regex(/^[A-Za-z0-9_-]+={0,2}$/) }),
  expirationTime: z.number().nullable().optional(),
})
export type PushSubscriptionInput = z.input<typeof PushSubscriptionInput>

export const PushSubscriptionRef = z.object({ endpoint: z.url({ protocol: /^https$/ }).max(2000) })

export const PushSubscription = z.object({ id: Uuid, endpoint: z.string(), createdAt: IsoTimestamp })
export type PushSubscription = z.output<typeof PushSubscription>

/**
 * `GET /push/vapid-key`: the application server key for `pushManager.subscribe` (null: push not configured) and its
 * id (`vapidKeyId`: the first 16 base64url characters of sha256(publicKey)). A browser whose subscription was made
 * under another key id resubscribes (docs/PWA.md "Rotating the VAPID key").
 */
export const VapidKey = z.object({ publicKey: z.string().nullable(), keyId: z.string().nullable().default(null) })
export type VapidKey = z.output<typeof VapidKey>

/**
 * Payload of one web push message (the service worker shows it and opens `url` on click). An approval push
 * (`kind: 'approval'`) also carries what the notification's Approve / Reject actions need: the approval id, the
 * Idempotency-Key the decision is posted with (one per approval, so a repeated tap never decides twice) and when the
 * approval expires (the worker refuses to act after it). `pending` is the user's count of approvals waiting (the app
 * badge).
 */
export const PushMessage = z.object({
  title: z.string(),
  body: z.string(),
  url: z.string(),
  tag: z.string(),
  kind: z.string(),
  icon: z.string().optional(),
  approvalId: z.string().optional(),
  idempotencyKey: z.string().max(200).optional(),
  expiresAt: z.string().optional(),
  pending: z.number().int().nonnegative().optional(),
})
export type PushMessage = z.output<typeof PushMessage>
